import { useEffect, useState } from 'react';

/**
 * `method` arrives as the display label the checkout resolved ("In Person",
 * "PayPal"). Older records and the legacy capitalize-first-letter path can send
 * "Inperson" or "Paypal", so collapse case and separators before matching.
 * "Cash App" folds to "cashapp" the same way.
 *
 * Mirrors normalizeMethod in PurchaseConfirmation.js.
 */
const normalizeMethod = (value) =>
  String(value ?? '').toLowerCase().replace(/[\s_-]/g, '');

const METHOD_LABELS = {
  zelle: 'Zelle',
  venmo: 'Venmo',
  cashapp: 'Cash App',
  paypal: 'PayPal',
  inperson: 'In Person',
  card: 'Card',
  free: 'Free',
};

/**
 * Glyph shown in the header medallion, keyed by normalized method. The
 * medallion keeps the page's quiet gray fill — only the pay CTA is allowed
 * to carry a brand color.
 */
const METHOD_ICONS = {
  zelle: 'fas fa-dollar-sign',
  venmo: 'fas fa-dollar-sign',
  cashapp: 'fas fa-dollar-sign',
  paypal: 'fab fa-paypal',
  inperson: 'fas fa-money-bill-wave',
  card: 'fab fa-cc-stripe',
  free: 'fas fa-check',
};

const FALLBACK_ICON = 'fas fa-dollar-sign';

// Only the deep-link methods need an accent; everything else has no CTA.
const PAY_ACCENT_CLASS = {
  venmo: 'venmo',
  cashapp: 'cashapp',
};

const PurchaseConfirmationMobile = () => {
  const [copied, setCopied] = useState(false);
  const [formattedNumber, setFormattedNumber] = useState(null)

  // Get the navigation params from window.history.state or props
  // In a real implementation, these would come from your router's state
  const navParams = window.history.state?.usr || window.history.state || {};
  const { methodID = 'payment@example.com', amount = 0, method } = navParams;

  const methodKey = normalizeMethod(method);
  const methodLabel = METHOD_LABELS[methodKey] || method;

  const isFree = methodKey === 'free';
  const isZelle = methodKey === 'zelle';
  const isVenmo = methodKey === 'venmo';
  const isCashApp = methodKey === 'cashapp';
  const isPayPal = methodKey === 'paypal';
  const isInPerson = methodKey === 'inperson';

  // Guard against a missing/non-numeric amount coming through nav state
  const safeAmount = Number(amount) || 0;

  const rawMethodID = String(methodID ?? '').trim();

  // Zelle and in-person surface a phone number; Venmo an @handle; Cash App a
  // $Cashtag; PayPal an email address.
  const recipientNoun = isVenmo
    ? 'address'
    : isCashApp
      ? 'Cashtag'
      : isPayPal
        ? 'email address'
        : 'number';

  // Both handle-based methods display a prefix that isn't part of the stored
  // value, so strip either sigil and re-add the right one for display.
  const isHandleMethod = isVenmo || isCashApp;
  const handlePrefix = isCashApp ? '$' : '@';
  const handle = isHandleMethod ? rawMethodID.replace(/^[@$]+/, '') : '';
  const displayedHandle = `${handlePrefix}${handle}`;

  /**
   * Venmo: https://venmo.com/<handle>?txn=pay&amount=<amount>
   *   txn=pay means the person opening the link pays the handle, and
   *   audience=private keeps the payment off the public Venmo feed.
   *
   * Cash App: https://cash.app/$<cashtag>/<amount>
   *   The amount is a path segment, not a query param, and there's no note or
   *   audience option. A zero total drops the segment so the link still opens
   *   the profile rather than a $0.00 request.
   *
   * No PayPal equivalent: paypal_id is always an email address, and paypal.me
   * links only resolve for a PayPal.me username. The email is copied and pasted
   * into PayPal's send-money screen instead.
   */
  const buildPayUrl = () => {
    if (!handle) return null;

    if (isVenmo) {
      return (
        `https://venmo.com/${encodeURIComponent(handle)}` +
        `?txn=pay&amount=${safeAmount.toFixed(2)}` +
        `&note=${encodeURIComponent('Verca purchase')}` +
        `&audience=private`
      );
    }

    if (isCashApp) {
      const base = `https://cash.app/$${encodeURIComponent(handle)}`;
      return safeAmount > 0 ? `${base}/${safeAmount.toFixed(2)}` : base;
    }

    return null;
  };

  const payUrl = buildPayUrl();

  const showPayButton = !!payUrl;

  const payAccentClass = PAY_ACCENT_CLASS[methodKey] || 'venmo';

  const displayedMethodID = isZelle
    ? formattedNumber
    : isHandleMethod
      ? displayedHandle
      : rawMethodID;

  // Venmo and Cash App copy the bare handle (that's what pastes into their
  // search fields); PayPal copies the email verbatim.
  const copyValue = isHandleMethod ? handle : rawMethodID;

  // In person is a physical cash/check handoff at the appointment — there's no
  // account or number to send money to, so no copy field at all.
  const showCopyField = !isFree && !isInPerson && !!rawMethodID;

  const methodIconClass = METHOD_ICONS[methodKey] || FALLBACK_ICON;

  useEffect(() => {
    setFormattedNumber(() => {
        return (formatMobileNumber(rawMethodID))
    })
  }, [rawMethodID])


    function formatMobileNumber(number) {
    // Remove all non-digit characters
    const cleaned = ('' + number).replace(/\D/g, '');

    // Handle different lengths
    if (cleaned.length === 10) {
      // Standard US format: (123) 456-7890
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      // US number with country code: +1 (234) 567-8901
      return cleaned.replace(/1(\d{3})(\d{3})(\d{4})/, '+1 ($1) $2-$3');
    } else if (cleaned.length > 11) {
      // Generic international format: +12 345 678 9012
      return '+' + cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4}).*/, '$1 $2 $3 $4');
    } else {
      // Fallback (incomplete input)
      return number;
    }
  }


  useEffect(() => {
    window.scrollTo(0, 0);
    // Animate success icon on mount
    const icon = document.querySelector('.success-icon');
    if (icon) {
      setTimeout(() => {
        icon.classList.add('animate');
      }, 100);
    }
  }, []);

  const handleLogoClick = () => {
    window.location.href = '/';
  };

  const handleCopyMethodID = () => {
    navigator.clipboard.writeText(String(copyValue));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cardTitle = isFree
    ? 'Thank you for using Verca!'
    : isInPerson
      ? 'Pay in Person'
      : `Complete Your Payment with ${methodLabel}`;

  const renderMessage = () => {
    if (isFree) {
      return 'Complimentary sessions have been added. Please check your email for details about any booked sessions.';
    }

    if (isInPerson) {
      return (
        <>
          Bring <span className="amount-highlight">${safeAmount.toFixed(2)}</span> in
          cash or check to your appointment. You’ll pay your provider directly when
          you arrive.
        </>
      );
    }

    if (showPayButton) {
      return (
        <>
          To complete your purchase, please send the payment totaling{' '}
          <span className="amount-highlight">${safeAmount.toFixed(2)}</span> via{' '}
          <span className="amount-highlight">{methodLabel}</span>:
        </>
      );
    }

    return (
      <>
        To complete your purchase, please send the payment totaling{' '}
        <span className="amount-highlight">${safeAmount.toFixed(2)}</span> to the{' '}
        {recipientNoun} below via{' '}
        <span className="amount-highlight">{methodLabel}</span>:
      </>
    );
  };

  return (
    <div className="confirmation-page">
      {/* Background elements */}
      <div className="background"></div>
      <div className="orb orb1"></div>
      <div className="orb orb2"></div>

      {/* Navigation */}
      <nav id="navbar">
        <div className="nav-container">
          <div className="logo" onClick={handleLogoClick}>
            <span className="logo-text">Verca</span>
          </div>
        </div>
      </nav>

      {/* Confirmation Content */}
      <section className="confirmation-section">
        <div className="confirmation-card">
          <div className={`success-icon ${isFree ? 'free-icon' : 'zelle-icon'}`}>
            <i className={methodIconClass}></i>
          </div>

          <h1 className="confirmation-title">{cardTitle}</h1>

          <p className="confirmation-message">{renderMessage()}</p>

          {/* The deep link is the primary path — button first, address as the fallback */}
          {showPayButton && (
            <a
              href={payUrl}
              className={`pay-button ${payAccentClass}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fas fa-dollar-sign"></i>
              <span>Pay ${safeAmount.toFixed(2)} with {methodLabel}</span>
            </a>
          )}

          {showPayButton && (
            <div className="or-divider"><span>or</span></div>
          )}

          {showCopyField && showPayButton && (
            <p className="address-label">Send payment to this {recipientNoun}</p>
          )}

          {showCopyField && (
            <div className="email-field" onClick={handleCopyMethodID}>
              <span className="email-text">{displayedMethodID}</span>
              <button className="copy-button">
                <i className={copied ? "fas fa-check" : "fas fa-copy"}></i>
                <span className="copy-tooltip">{copied ? "Copied!" : "Copy"}</span>
              </button>
            </div>
          )}

          {/* <p className="confirmation-message">
            An invoice receipt will be emailed to you and the service provider upon confirmation.
          </p> */}


          <div className="app-benefits">
            <h3 className="benefits-title">Track your sessions and get the most out of Verca with our app:</h3>
            <ul className="benefits-list">
              <li className="benefit-item">
                <i className="fas fa-check-circle"></i>
                Session tracking
              </li>
              <li className="benefit-item">
                <i className="fas fa-check-circle"></i>
                Up-to-date offers & packages
              </li>
              <li className="benefit-item">
                <i className="fas fa-check-circle"></i>
                In-app checkout
              </li>
              <li className="benefit-item">
                <i className="fas fa-check-circle"></i>
                Community access
              </li>
            </ul>
          </div>

          <div className="action-buttons">
            <a href="https://apps.apple.com/us/app/verca/id6751428364" className="app-store-button" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-apple"></i>
              <div className="app-store-text">
                <span className="app-store-small">Download on the</span>
                <span className="app-store-large">App Store</span>
              </div>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.arthurhakobyandev.StreamChatDev" className="google-play-button" target="_blank" rel="noopener noreferrer">
              <img
                src="/gplay.png"
                alt="Get it on Google Play"
                className="google-play-badge"
              />
              <div className="app-store-text">
                <span className="app-store-small">Get it on</span>
                <span className="app-store-large">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/*
        GLOBAL block. styled-jsx scopes every selector to this component, which
        means :root, html and body rules in a plain `<style jsx>` block are
        rewritten to selectors that never match and are silently dropped.
        Page-level background and the design tokens have to live here.
      */}
      <style jsx global>{`
        :root {
            /* Page canvas — soft gray behind the white card */
            --page-bg: #F5F6F8;

            /* Core colors */
            --bg-primary: #F5F6F8;
            --bg-secondary: #FAFAFA;
            --bg-tertiary: #F6F6F6;

            /* Surface colors */
            --surface: #FFFFFF;
            --surface-light: #F6F6F6;
            --surface-border: #E6E6E6;

            /* Soft gray fills — quiet chrome so the one blue CTA carries the page */
            --fill-soft: #F2F3F5;
            --fill-soft-hover: #E8EAED;
            --fill-soft-border: #E2E4E8;
            --on-fill-soft: #1F2124;

            /* Text colors */
            --text-primary: #1F2124;
            --text-secondary: #767676;
            --text-tertiary: #A3A3A3;
            --text-muted: #767676;

            /* Venmo brand blue — the accent when Venmo is the pay method */
            --venmo: #008CFF;
            --venmo-hover: #0072D6;
            --venmo-ring: rgba(0, 140, 255, 0.32);

            /* Cash App brand green — the accent when Cash App is the pay method.
               White on this green fails contrast, so the label goes dark ink. */
            --cashapp: #00D632;
            --cashapp-hover: #00BE2C;
            --cashapp-ring: rgba(0, 214, 50, 0.32);
            --on-cashapp: #0A2412;

            /* Legacy aliases */
            --success: #1F2124;
            --primary: #1F2124;
            --zelle: #1F2124;
            --free-green: #1F2124;
        }

        html {
            scroll-behavior: smooth;
            background: var(--page-bg);
        }

        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Lato', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background: var(--page-bg);
            color: var(--text-primary);
            overflow-x: hidden;
            line-height: 1.6;
        }

        /* Covers app shells that paint their own dark background on the mount node */
        #root,
        #__next {
            background: var(--page-bg);
        }
      `}</style>

      <style jsx>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            -webkit-tap-highlight-color: transparent;
        }

        /* Owns the full viewport so the gray wins even if an ancestor
           in the app shell paints something darker. */
        .confirmation-page {
            min-height: 100vh;
            background: var(--page-bg, #F5F6F8);
        }

        /* Background */
        .background {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: var(--page-bg, #F5F6F8);
            z-index: -1;
        }

        /* Ambient orbs retired with the flat palette — kept in the DOM but not drawn. */
        .orb,
        .orb1,
        .orb2 {
            display: none;
        }

        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            padding: 20px 40px;
            background: #FFFFFF;
            border-bottom: 1px solid #EDEDED;
            z-index: 1000;
        }

        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            align-items: center;
        }

        .logo {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 28px;
            font-weight: 800;
            letter-spacing: -1px;
            cursor: pointer;
        }

        .logo-icon {
            width: 44px;
            height: 44px;
            background: var(--fill-soft, #F2F3F5);
            border: 1px solid var(--fill-soft-border, #E2E4E8);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: none;
        }

        .logo-icon i {
            font-size: 22px;
            color: var(--on-fill-soft, #1F2124);
        }

        .logo-text {
            color: var(--text-primary, #1F2124);
        }

        /* Confirmation Section */
        .confirmation-section {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 120px 40px 60px;
            background: var(--page-bg, #F5F6F8);
        }

        .confirmation-card {
            max-width: 520px;
            width: 100%;
            background: #FFFFFF;
            border: 1px solid #E9EBEE;
            border-radius: 24px;
            box-shadow: 0 1px 2px rgba(31, 33, 36, 0.04),
                        0 10px 30px rgba(31, 33, 36, 0.06);
            padding: 56px;
            text-align: center;
            position: relative;
            overflow: hidden;
            opacity: 0;
            animation: cardFadeIn 0.6s ease-out forwards;
            animation-delay: 0.2s;
        }

        @keyframes cardFadeIn {
            to {
                opacity: 1;
                transform: translateY(0);
            }
            from {
                opacity: 0;
                transform: translateY(20px);
            }
        }

        /* Success Icon */
        .success-icon {
            width: 100px;
            height: 100px;
            margin: 0 auto 28px;
            background: var(--fill-soft, #F2F3F5);
            border: 1px solid var(--fill-soft-border, #E2E4E8);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            opacity: 0;
            transform: scale(0);
            transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .success-icon.zelle-icon {
            background: var(--fill-soft, #F2F3F5);
        }

        .success-icon.free-icon {
            background: var(--fill-soft, #F2F3F5);
        }

        .success-icon.free-icon i {
            color: var(--on-fill-soft, #1F2124);
        }

        .success-icon.free-icon::before {
            display: none;
        }

        .success-icon.animate {
            opacity: 1;
            transform: scale(1);
        }

        .success-icon::before {
            display: none;
        }

        .success-icon i {
            font-size: 40px;
            color: var(--on-fill-soft, #1F2124);
            position: relative;
            z-index: 1;
        }

        /* Typography */
        .confirmation-title {
            font-size: 32px;
            font-weight: 700;
            margin-bottom: 20px;
            letter-spacing: -0.5px;
            line-height: 1.2;
            color: var(--text-primary, #1F2124);
        }

        .confirmation-message {
            font-size: 16px;
            color: var(--text-secondary, #767676);
            line-height: 1.6;
            letter-spacing: 0.3px;
            margin-bottom: 28px;
        }

        .amount-highlight {
            color: var(--text-primary, #1F2124);
            font-weight: 600;
            font-size: 18px;
        }

        /* Pay CTA — primary action, so it leads and carries the only color.
           The accent follows the method: Venmo blue, Cash App green. */
        .pay-button {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            width: 100%;
            padding: 17px 24px;
            border-radius: 14px;
            text-decoration: none;
            font-size: 17px;
            font-weight: 600;
            letter-spacing: 0.2px;
            transition: all 0.25s ease;
        }

        .pay-button:active {
            transform: translateY(0);
        }

        .pay-button i {
            font-size: 18px;
        }

        .pay-button.venmo {
            background: var(--venmo, #008CFF);
            border: 1px solid var(--venmo, #008CFF);
            color: #FFFFFF;
        }

        .pay-button.venmo:hover {
            background: var(--venmo-hover, #0072D6);
            border-color: var(--venmo-hover, #0072D6);
            transform: translateY(-2px);
        }

        .pay-button.venmo:focus-visible {
            outline: 3px solid var(--venmo-ring, rgba(0, 140, 255, 0.32));
            outline-offset: 2px;
        }

        .pay-button.cashapp {
            background: var(--cashapp, #00D632);
            border: 1px solid var(--cashapp, #00D632);
            color: var(--on-cashapp, #0A2412);
        }

        .pay-button.cashapp:hover {
            background: var(--cashapp-hover, #00BE2C);
            border-color: var(--cashapp-hover, #00BE2C);
            transform: translateY(-2px);
        }

        .pay-button.cashapp:focus-visible {
            outline: 3px solid var(--cashapp-ring, rgba(0, 214, 50, 0.32));
            outline-offset: 2px;
        }

        /* OR divider */
        .or-divider {
            display: flex;
            align-items: center;
            gap: 16px;
            margin: 24px 0 20px;
            color: var(--text-tertiary, #A3A3A3);
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 1.4px;
            text-transform: uppercase;
        }

        .or-divider::before,
        .or-divider::after {
            content: '';
            flex: 1;
            height: 1px;
            background: #E6E6E6;
        }

        .address-label {
            font-size: 14px;
            color: var(--text-secondary, #767676);
            margin-bottom: 12px;
            letter-spacing: 0.2px;
        }

        /* Email Field */
        .email-field {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 18px;
            font-weight: 500;
            color: var(--text-primary, #1F2124);
            padding: 16px 20px;
            background: #FFFFFF;
            border-radius: 14px;
            border: 1px solid #D6D6D6;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-bottom: 24px;
        }

        .email-field:hover {
            background: var(--bg-secondary, #FAFAFA);
            border-color: #A9AEB5;
            transform: translateY(-2px);
        }

        .email-text {
            word-break: break-all;
            flex: 1;
            text-align: left;
        }

        .copy-button {
            background: none;
            border: none;
            color: var(--text-secondary, #767676);
            cursor: pointer;
            padding: 4px 8px;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: color 0.3s ease;
            position: relative;
        }

        .copy-button:hover {
            color: var(--text-primary, #1F2124);
        }

        .copy-button i {
            font-size: 16px;
        }

        .copy-tooltip {
            font-size: 12px;
            font-weight: 500;
        }

        /* Payment Instructions */
        .payment-instructions {
            background: var(--fill-soft, #F2F3F5);
            border: 1px solid var(--fill-soft-border, #E2E4E8);
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 32px;
        }

        .instruction-text {
            font-size: 14px;
            color: var(--text-secondary, #767676);
            line-height: 1.5;
            display: flex;
            align-items: flex-start;
            gap: 10px;
            text-align: left;
        }

        .instruction-text i {
            color: var(--on-fill-soft, #1F2124);
            margin-top: 2px;
            font-size: 16px;
            flex-shrink: 0;
        }

        /* App Benefits */
        .app-benefits {
            margin: 32px 0;
            text-align: left;
        }

        .benefits-title {
            font-size: 18px;
            font-weight: 600;
            color: var(--text-primary, #1F2124);
            margin-bottom: 20px;
            text-align: center;
        }

        .benefits-list {
            list-style: none;
            margin-bottom: 32px;
        }

        .benefit-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 0;
            color: #444444;
            font-size: 15px;
            border-bottom: 1px solid #EDEDED;
        }

        .benefit-item:last-child {
            border-bottom: none;
        }

        .benefit-item i {
            color: var(--text-tertiary, #A3A3A3);
            font-size: 16px;
        }

        /* Action Buttons */
        .action-buttons {
            display: flex;
            gap: 20px;
            justify-content: center;
            flex-wrap: wrap;
            margin-top: 24px;
        }

        /* App Store Buttons — softened to gray so they read as secondary */
        .app-store-button {
            display: inline-flex;
            align-items: center;
            gap: 14px;
            padding: 14px 24px;
            background: var(--fill-soft, #F2F3F5);
            border: 1px solid var(--fill-soft-border, #E2E4E8);
            border-radius: 14px;
            color: var(--on-fill-soft, #1F2124);
            text-decoration: none;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
            min-width: 180px;
        }

        .app-store-button::before {
            display: none;
        }

        .app-store-button:hover {
            transform: translateY(-3px);
            background: var(--fill-soft-hover, #E8EAED);
            border-color: #D5D8DD;
        }

        .app-store-button i {
            font-size: 32px;
            position: relative;
            z-index: 1;
        }

        .google-play-button {
            display: inline-flex;
            align-items: center;
            gap: 14px;
            padding: 14px 24px;
            background: var(--fill-soft, #F2F3F5);
            border: 1px solid var(--fill-soft-border, #E2E4E8);
            border-radius: 14px;
            color: var(--on-fill-soft, #1F2124);
            text-decoration: none;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
            min-width: 180px;
        }

        .google-play-button::before {
            display: none;
        }

        .google-play-button:hover {
            transform: translateY(-3px);
            background: var(--fill-soft-hover, #E8EAED);
            border-color: #D5D8DD;
        }

        .google-play-badge {
            width: 32px;
            height: 32px;
            object-fit: contain;
            position: relative;
            z-index: 1;
        }

        .app-store-text {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
            z-index: 1;
        }

        .app-store-small {
            font-size: 12px;
            font-weight: 400;
            color: var(--text-secondary, #767676);
            letter-spacing: 0.4px;
            text-transform: uppercase;
        }

        .app-store-large {
            font-size: 20px;
            font-weight: 600;
            letter-spacing: 0.3px;
            margin-top: -2px;
        }

        @media (prefers-reduced-motion: reduce) {
            .confirmation-card { animation-duration: 1ms; }
            .email-field:hover,
            .pay-button:hover,
            .app-store-button:hover,
            .google-play-button:hover { transform: none; }
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
            nav {
                padding: 16px 24px;
            }

            .confirmation-section {
                padding: 100px 24px 40px;
            }

            .confirmation-card {
                padding: 48px 32px;
            }

            .success-icon {
                width: 100px;
                height: 100px;
            }

            .success-icon i {
                font-size: 40px;
            }

            .confirmation-title {
                font-size: 28px;
            }

            .confirmation-message {
                font-size: 16px;
            }

            .amount-highlight {
                font-size: 17px;
            }

            .pay-button {
                font-size: 16px;
                padding: 16px 20px;
            }

            .email-field {
                font-size: 16px;
                padding: 14px 18px;
            }

            .email-text {
                font-size: 15px;
            }

            .action-buttons {
                flex-direction: column;
                align-items: center;
            }

            .app-store-button,
            .google-play-button {
                width: 100%;
                max-width: 280px;
                padding: 12px 20px;
                justify-content: center;
            }

            .google-play-badge {
                width: 28px;
                height: 28px;
            }
        }

        @media (max-width: 480px) {
            .confirmation-card {
                padding: 40px 24px;
                border-radius: 24px;
            }

            .confirmation-title {
                font-size: 24px;
            }

            .confirmation-message {
                font-size: 15px;
            }

            .amount-highlight {
                font-size: 16px;
            }

            .pay-button {
                font-size: 15px;
                padding: 15px 18px;
            }

            .or-divider {
                margin: 20px 0 16px;
            }

            .address-label {
                font-size: 13px;
            }

            .email-field {
                font-size: 14px;
                padding: 12px 16px;
            }

            .email-text {
                font-size: 13px;
            }

            .benefits-title {
                font-size: 16px;
            }

            .benefit-item {
                font-size: 14px;
            }

            .action-buttons {
                flex-direction: column;
                align-items: center;
                gap: 16px;
            }

            .app-store-button,
            .google-play-button {
                width: 100%;
                max-width: 280px;
                padding: 12px 20px;
                justify-content: center;
            }

            .app-store-button i {
                font-size: 28px;
            }

            .google-play-badge {
                width: 28px;
                height: 28px;
            }
        }
      `}</style>

      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
    </div>
  );
};

export default PurchaseConfirmationMobile;